import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import useEventCallback from '@ecp/common/src/hooks/useEventCallback';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Input, { StyledErrorMessage } from '@ecp/common/src/components/input/bo/Input';
import RadioGroup from '@ecp/common/src/components/radio/bo/RadioGroup';
import Popup from '@ecp/common/src/components/modal/bo/Popup';

import { extractForm } from '@bo-utils/formUtils';
import useValidate from '@bo-hooks/common/useValidate';
import { useConfirmDialog } from '@bo-hooks/common/useModal';
import siteApi from '@bo-apis/system/siteApi';
import FormField from '@bo-components/common/form/FormField';
import Form from '@bo-components/common/form/Form';
import { UNSAVED_CONFIRM_MESSAGE } from '@bo-pages/system/consts/SystemConsts';
import { diviOptions, initialErrors, initialForm, validators } from '../consts/CustomerCenterConst';

const WelmallCustInfo = ({ welmallCustCenter }) => {
  return useMemo(
    () => (
      <TextBox style={{ whiteSpace: 'pre-wrap' }}>
        {`고객센터 전화번호 : ${welmallCustCenter.customerCenterTel}\n` +
          `운영시간 : 평일 ${welmallCustCenter.customerCenterOperatingStartHh} ~ ${welmallCustCenter.customerCenterOperatingEndHh}\n` +
          `점심시간 : ${welmallCustCenter.customerCenterLunchStartHh} ~ ${welmallCustCenter.customerCenterLunchEndHh}\n` +
          `${welmallCustCenter.customerCenterGuide}`}
      </TextBox>
    ),
    [welmallCustCenter]
  );
};

const getFormData = (info, isDirect) => {
  if (isDirect) {
    return {
      customerCenterTel: info.customerCenterTel,
        customerCenterOperatingStartHh: info.customerCenterOperatingStartHh,
      customerCenterOperatingEndHh: info.customerCenterOperatingEndHh,
      customerCenterLunchStartHh: info.customerCenterLunchStartHh,
      customerCenterLunchEndHh: info.customerCenterLunchEndHh,
      customerCenterGuide: info.customerCenterGuide,
      welmallCustomerCenterUseYn: info.welmallCustomerCenterUseYn,
    }
  } else {
    return {
      customerCenterTel: info.customerCenterTel,
      customerCenterOperatingStartHh: info.customerCenterOperatingStartHh,
      customerCenterOperatingEndHh: info.customerCenterOperatingEndHh,
      customerCenterLunchStartHh: info.customerCenterLunchStartHh,
      customerCenterLunchEndHh: info.customerCenterLunchEndHh,
      customerCenterGuide: info.customerCenterGuide,
      welmallCustomerCenterUseYn: 'Y',
    }
  }
};

const CustomerCenterPopup = ({ open, onClose, siteBasicInfo, welmallCustCenter, refreshCustomerCenter }) => {
  const [form, setForm] = useState({ ...initialForm });
  const [error, setError] = useState({ ...initialErrors });
  const [hasChangedData, setHasChangedData] = useState(false);
  const { formValidate } = useValidate();
  const { showConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    if (open) setHasChangedData(false);
    if (open && siteBasicInfo.welmallCustomerCenterUseYn === 'N') {
      setForm(getFormData(siteBasicInfo, true));
    } else {
      setForm({ ...initialForm });
    }
  }, [siteBasicInfo, open]);

  const handleFormChange = useEventCallback((v, data) => {
    setHasChangedData(true);
    setForm((prev) => ({ ...prev, [data.key]: v }));
  });
  const handleCustomerCenter = async () => {
    let formData = {};
    if (form.welmallCustomerCenterUseYn === 'Y') {
      formData = getFormData(welmallCustCenter, false);
    } else {
      const validated = await formValidate('updated', form, setError, validators);
      if (!validated) return;
      formData = Object.keys(form).reduce((prev, key) => {
        prev[key] = extractForm(form[key]);
        return prev;
      }, {});
    }

    showConfirmDialog('저장하시겠습니까?', () => updateSiteCustomerCenter(formData));
  };

  const updateSiteCustomerCenter = async (formData) => {
    await siteApi.updateSiteCustomerCenter({ ...siteBasicInfo, ...formData });
    refreshCustomerCenter();
    onClose();
  };

  const closePopup = () => {
    if (hasChangedData) {
      showConfirmDialog(UNSAVED_CONFIRM_MESSAGE, onClose);
      return;
    }
    onClose();
  };

  return (
    <Popup
      type={'Medium'}
      open={open}
      header={'고객센터수정'}
      onConfirmClick={handleCustomerCenter}
      onClose={closePopup}
      onCancelClick={closePopup}
      confirmButtonText={'저장'}
    >
      <Form labelSize={'120px'}>
        <FormField label={'구분'} full>
          <RadioGroup
            data-key={'welmallCustomerCenterUseYn'}
            itemList={diviOptions}
            value={form.welmallCustomerCenterUseYn}
            onClick={handleFormChange}
          />
        </FormField>
        <FormField label={'내용'} full>
          {form.welmallCustomerCenterUseYn === 'Y' ? (
            <WelmallCustInfo welmallCustCenter={welmallCustCenter} />
          ) : (
            <>
              <FlexBoxColumn width={'100%'} align-items={'flex-start'}>
                <FlexBox>
                  <TextBox required gap={'3px'} width={'130px'}>
                    고객센터 전화번호
                  </TextBox>
                  <Input
                    width={'332px'}
                    data-key={'customerCenterTel'}
                    value={form.customerCenterTel}
                    maxLength={30}
                    error={!!error.customerCenterTel}
                    errorMessage={error.customerCenterTel}
                    onChange={handleFormChange}
                    placeholder={`'-'포함 입력`}
                  />
                </FlexBox>
                <Spacing bottom={'6px'} />
                <FlexBox width={'100%'}>
                  <TextBox required gap={'3px'} width={'130px'}>
                    운영시간
                  </TextBox>
                  <FlexBoxColumn align-items={'flex-start'}>
                    <FlexBox>
                      <Input
                        minWidth={'128px'}
                        width={'128px'}
                        placeholder={'00:00'}
                        data-key={'customerCenterOperatingStartHh'}
                        value={form.customerCenterOperatingStartHh}
                        error={!!error.customerCenterOperatingStartHh}
                        maxLength={50}
                        onChange={handleFormChange}
                      />
                      <TextBox padding={'0 6px'}>-</TextBox>
                      <Input
                        minWidth={'128px'}
                        width={'128px'}
                        placeholder={'00:00'}
                        data-key={'customerCenterOperatingEndHh'}
                        value={form.customerCenterOperatingEndHh}
                        error={!!error.customerCenterOperatingEndHh}
                        maxLength={50}
                        onChange={handleFormChange}
                      />
                    </FlexBox>
                    {(error.customerCenterOperatingStartHh || error.customerCenterOperatingEndHh) && (
                      <StyledErrorMessage>
                        {error.customerCenterOperatingStartHh || error.customerCenterOperatingEndHh}
                      </StyledErrorMessage>
                    )}
                  </FlexBoxColumn>
                </FlexBox>
                <Spacing bottom={'6px'} />
                <FlexBox>
                  <TextBox required gap={'3px'} width={'130px'}>
                    점심시간
                  </TextBox>
                  <FlexBoxColumn align-items={'flex-start'}>
                    <FlexBox>
                      <Input
                        minWidth={'128px'}
                        width={'128px'}
                        placeholder={'00:00'}
                        data-key={'customerCenterLunchStartHh'}
                        value={form.customerCenterLunchStartHh}
                        maxLength={50}
                        error={!!error.customerCenterLunchStartHh}
                        onChange={handleFormChange}
                      />
                      <TextBox padding={'0 6px'}>-</TextBox>
                      <Input
                        minWidth={'128px'}
                        width={'128px'}
                        placeholder={'00:00'}
                        data-key={'customerCenterLunchEndHh'}
                        value={form.customerCenterLunchEndHh}
                        maxLength={50}
                        error={!!error.customerCenterLunchEndHh}
                        onChange={handleFormChange}
                      />
                    </FlexBox>
                    {(error.customerCenterLunchStartHh || error.customerCenterLunchEndHh) && (
                      <StyledErrorMessage>
                        {error.customerCenterLunchStartHh || error.customerCenterLunchEndHh}
                      </StyledErrorMessage>
                    )}
                  </FlexBoxColumn>
                </FlexBox>
                <Spacing bottom={'6px'} />
                <FlexBox>
                  <TextBox required gap={'3px'} width={'130px'}>
                    안내사항
                  </TextBox>
                  <Input
                    width={'332px'}
                    data-key={'customerCenterGuide'}
                    value={form.customerCenterGuide}
                    maxLength={1000}
                    placeholder={'휴무일 또는 특이사항 입력'}
                    error={!!error.customerCenterGuide}
                    errorMessage={error.customerCenterGuide}
                    onChange={handleFormChange}
                  />
                </FlexBox>
              </FlexBoxColumn>
            </>
          )}
        </FormField>
      </Form>
    </Popup>
  );
};

export default CustomerCenterPopup;

CustomerCenterPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  refreshCustomerCenter: PropTypes.func,
  siteBasicInfo: PropTypes.object,
  welmallCustCenter: PropTypes.object,
};
